import React, { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { X } from "@phosphor-icons/react";

/**
 * P4-X3 (2026-02) — Card Change Request modal.
 *
 * Customer request types (locked by backend FIELD literal):
 *   driver_name · phone · unit · status
 * (PIN is an admin-only field and is NOT surfaced here.)
 *
 * Every submission carries an `effective_date` (YYYY-MM-DD, today or
 * future). The backend enforces:
 *   - customer role cannot request `customer_transfer` or `customer_id`
 *   - Unit destination must belong to authenticated customer
 *   - backdated effective_date is rejected for customer role
 *   - duplicate pending request on same (card_id, field_name) is rejected
 *
 * On admin approve of a request carrying `effective_date`:
 *   - future date → history event only (P4-X2 resolver applies at tran_date)
 *   - today       → history event + $set on cards.*
 */
const FIELD_LABELS = {
  driver_name: "Driver Name",
  unit: "Unit #",
  phone: "Phone / Mobile #",
  status: "Status",
};

const FIELD_PLACEHOLDERS = {
  driver_name: "Driver name",
  phone: "e.g. +1 416-555-0123",
};

// Operator-facing Status choices — canonicalised by backend to
// {active, hold, inactive}. See card_change_requests.STATUS_LABEL_TO_CANONICAL.
const STATUS_CHOICES = [
  { value: "active",     label: "Active" },
  { value: "hold",       label: "Hold" },
  { value: "deactivate", label: "Deactivate" },
];

function _todayIso() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${dd}`;
}

export default function CardChangeRequestModal({ card, onClose, onSubmitted }) {
  const [fieldName, setFieldName] = useState("driver_name");
  const [newValue, setNewValue] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(_todayIso());
  const [submitting, setSubmitting] = useState(false);
  const [ack, setAck] = useState(null);

  // P4-X3 §6 — Unit destination dropdown must come from the
  // authenticated customer's own units. The backend derives the
  // scope from the session token; the frontend does NOT pass a
  // customer_id parameter.
  const [unitOptions, setUnitOptions] = useState([]);
  const [unitOptionsLoading, setUnitOptionsLoading] = useState(false);

  useEffect(() => {
    if (fieldName !== "unit") return;
    setUnitOptionsLoading(true);
    api.get("/portal/units")
      .then(({ data }) => {
        const rows = Array.isArray(data) ? data : (data?.units || []);
        setUnitOptions(
          rows
            .map((r) => (typeof r === "string" ? r : (r.unit || r.value || "")))
            .filter((u) => u && u !== (card?.unit || "")),
        );
      })
      .catch(() => setUnitOptions([]))
      .finally(() => setUnitOptionsLoading(false));
  }, [fieldName, card?.unit]);

  const oldValue = useMemo(() => {
    if (fieldName === "driver_name") return card?.driver_id || card?.driver_name || "";
    if (fieldName === "unit") return card?.unit || "";
    if (fieldName === "phone") return card?.phone || "";
    if (fieldName === "status") return card?.status || "active";
    return "";
  }, [fieldName, card]);

  const submit = async () => {
    const trimmed = (newValue || "").trim();
    if (!trimmed) {
      toast.error("Please enter a new value.");
      return;
    }
    if (trimmed === oldValue) {
      toast.error("New value matches the current value.");
      return;
    }
    if (!effectiveDate) {
      toast.error("Effective date is required.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/portal/card-change-requests", {
        card_id: card.card_id,
        field_name: fieldName,
        new_value: trimmed,
        effective_date: effectiveDate,
      });
      setAck(data.acknowledgement || "Request submitted.");
      if (onSubmitted) onSubmitted(data);
    } catch (e) {
      const detail = e?.response?.data?.detail || "Could not submit request.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  // Reset input on field change.
  const changeField = (v) => {
    setFieldName(v);
    setNewValue("");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      data-testid="ccr-modal"
    >
      <div className="bg-white w-full max-w-lg rounded shadow-lg border border-zinc-200">
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
          <div>
            <div className="overline text-[10px]">CARD CHANGE REQUEST</div>
            <div className="font-medium text-sm">
              Card ending {(card?.card_number || "").slice(-5) || "—"} · {(card?.network || "").toUpperCase()}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-700"
            data-testid="ccr-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        {ack ? (
          <div className="px-5 py-6" data-testid="ccr-modal-ack">
            <div className="overline text-[10px] text-emerald-600 mb-2">SUBMITTED</div>
            <div className="text-sm text-zinc-800">{ack}</div>
            <div className="mt-5 flex justify-end">
              <button
                onClick={onClose}
                className="gf-btn gf-btn-primary"
                data-testid="ccr-modal-done"
              >
                Done
              </button>
            </div>
          </div>
        ) : (
          <div className="px-5 py-5 space-y-4">
            <div>
              <label className="overline text-[10px]">FIELD TO CHANGE</label>
              <select
                value={fieldName}
                onChange={(e) => changeField(e.target.value)}
                className="gf-input w-full"
                data-testid="ccr-field-name"
              >
                <option value="driver_name">{FIELD_LABELS.driver_name}</option>
                <option value="unit">{FIELD_LABELS.unit}</option>
                <option value="phone">{FIELD_LABELS.phone}</option>
                <option value="status">{FIELD_LABELS.status}</option>
              </select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-[10px]">CURRENT</label>
                <div
                  className="gf-input font-mono text-sm bg-zinc-50 text-zinc-500"
                  data-testid="ccr-current-value"
                >
                  {oldValue || "—"}
                </div>
              </div>
              <div>
                <label className="overline text-[10px]">REQUESTED</label>
                {fieldName === "unit" ? (
                  <select
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    className="gf-input w-full"
                    data-testid="ccr-new-value"
                  >
                    <option value="">
                      {unitOptionsLoading ? "Loading…"
                        : unitOptions.length === 0 ? "No other units available"
                        : "Select unit…"}
                    </option>
                    {unitOptions.map((u) => (
                      <option key={u} value={u}>{u}</option>
                    ))}
                  </select>
                ) : fieldName === "status" ? (
                  <select
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    className="gf-input w-full"
                    data-testid="ccr-new-value"
                  >
                    <option value="">Select status…</option>
                    {STATUS_CHOICES.map((s) => (
                      <option key={s.value} value={s.value}>{s.label}</option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    className="gf-input w-full"
                    placeholder={FIELD_PLACEHOLDERS[fieldName]}
                    data-testid="ccr-new-value"
                  />
                )}
              </div>
            </div>
            <div>
              <label className="overline text-[10px]">EFFECTIVE DATE</label>
              <input
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                className="gf-input w-full"
                data-testid="ccr-effective-date"
              />
              <div className="text-xs text-zinc-500 mt-1">
                If approved, the change applies from the selected effective
                date. Earlier history remains under the prior details. Unit
                changes cannot be backdated across transactions that have
                already been invoiced.
              </div>
            </div>
            <div className="text-xs text-zinc-500">
              Requests are reviewed by your account manager within 24 hours.
              You&apos;ll see the status on the <strong>Change Requests</strong> page.
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={onClose}
                className="gf-btn gf-btn-outline"
                data-testid="ccr-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={submitting}
                className="gf-btn gf-btn-primary"
                data-testid="ccr-submit"
              >
                {submitting ? "Submitting…" : "Submit request"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
